import { createContext, useState, useMemo, useEffect } from "react";
import type { Dispatch, SetStateAction, ReactNode } from "react";
import { clientOnly$ } from "vite-env-only/macros";

interface TrackingContext {
  jobSeekerProfileId: string | null;
  applicantEmail: string | null;
  setJobSeekerProfileId?: Dispatch<SetStateAction<string | null>>;
  setApplicantEmail?: Dispatch<SetStateAction<string | null>>;
}

export const TrackingContext = createContext<TrackingContext>({
  jobSeekerProfileId: null,
  applicantEmail: null,
} as TrackingContext);

const canUseSessionStorage = () => {
  try {
    sessionStorage.setItem("gh-test", "gh-test");
    sessionStorage.getItem("gh-test");
    return true;
  } catch (e) {
    return false;
  }
};

const retrieveFromStorage = clientOnly$((key: string) => {
  const storage = canUseSessionStorage() ? sessionStorage.getItem("gh-tracking") : null;
  if (storage) {
    try {
      const parsed = JSON.parse(storage);
      return parsed[key] || null;
    } catch (err) {
      return null;
    }
  }
});

export const TrackingContextProvider = ({ children }: { children: ReactNode }) => {
  const [jobSeekerProfileId, setJobSeekerProfileId] = useState(
    retrieveFromStorage ? retrieveFromStorage("profileId") : null
  );
  const [applicantEmail, setApplicantEmail] = useState(
    retrieveFromStorage ? retrieveFromStorage("email") : null
  );

  const [useStorage] = useState(canUseSessionStorage());

  const value = useMemo(() => {
    return {
      jobSeekerProfileId,
      setJobSeekerProfileId,
      applicantEmail,
      setApplicantEmail,
    };
  }, [jobSeekerProfileId, setJobSeekerProfileId, applicantEmail, setApplicantEmail]);

  useEffect(() => {
    if (useStorage && (jobSeekerProfileId || applicantEmail)) {
      try {
        sessionStorage.setItem(
          "gh-tracking",
          JSON.stringify({
            profileId: jobSeekerProfileId,
            email: applicantEmail,
          })
        );
      } catch {
        return;
      }
    }
  }, [useStorage, jobSeekerProfileId, applicantEmail]);

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};
